/* ============================================================================
   ADMIN → FORMULAS → EDIT (PROTOTAIP DESIGN · 27 Okt 2026, ADMIN SAHAJA)
   ============================================================================
   Dibuka dari gear card dalam FormulaAdminView. Tab:
     General · Card · Prompt · Customer options · Robot · [Extras: GOYANG / SMART / FLOWNITY] · Test · History
   ⭐ PARITI (arahan user "pastikan tak ada yg hilang setting"): setiap medan popup Module Settings
      sedia ada + Create module + medan tanpa UI hari ini ada tempat di sini — senarai pemetaan dalam
      kepala utils/formulaAdminContoh.js. Tab Prompt = FormulaAdminPrompt.jsx · Extras = FormulaAdminExtras.jsx.
   ⛔ RUPA SAHAJA: semua pilihan = keadaan tempatan (hilang bila reload). Save / Run test / Restore /
      Delete tidak menulis apa-apa — memapar nota "Design prototype". Tiada Firestore, tiada callable.
   ========================================================================== */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import '../design/tokens.css';
import { PageHeader, Segmented, Btn, Badge, EmptyState } from '../design/ui';
import './FormulaAdmin.css';
import { Pils, Medan, Kotak, BarisSuis } from './FormulaAdminUi';
import FormulaAdminPrompt from './FormulaAdminPrompt';
import FormulaAdminExtras from './FormulaAdminExtras';
import { GOAL_V2, FORMAT_V2, PIL_V2 } from '../utils/generateV2Matlamat';
import {
    FORMULA_CONTOH, FORMULA_BARU, STATUS_FA, AKSES_FA, ENJIN_FA, TETAPAN_BLOCK_CONTOH, PILIHAN_CUSTOMER, ROBOT_CONTOH,
    PRODUK_UJIAN_CONTOH, SEJARAH_CONTOH, MOD_PROMPT,
} from '../utils/formulaAdminContoh';

const NOTA_PROTOTAIP = 'Design prototype — nothing is saved or run yet.';
const LABEL_EXTRAS = { goyang: 'Templates', smart: 'ChatGPT templates', flownity: 'Scrape agent' };
const BINAAN = new Set(['ai8saat', 'ai16saat-half', 'homeniche', 'goyang', 'smartcustom']);

export default function FormulaAdminEditView() {
    const { formulaId } = useParams();
    const navigate = useNavigate();
    const asal = formulaId === 'baru' ? FORMULA_BARU : FORMULA_CONTOH.find((f) => f.id === formulaId);
    if (!asal) {
        return (
            <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>
                <EmptyState title="Formula not found" action={<Btn onClick={() => navigate('/admin/formulas')}>Back to Formula Settings</Btn>} />
            </div>
        );
    }
    return <Editor key={asal.id} asal={asal} onKembali={() => navigate('/admin/formulas')} />;
}

function Editor({ asal, onKembali }) {
    const baru = asal.id === 'baru';
    const [f, setF] = useState(() => ({ ...asal, tetapanAwal: TETAPAN_BLOCK_CONTOH[asal.id] || {} }));
    const [tab, setTab] = useState('prompt');
    const [nota, setNota] = useState('');
    const ubah = (k, v) => { setF((x) => ({ ...x, [k]: v })); setNota(''); };
    const simpan = () => setNota(NOTA_PROTOTAIP);

    const [opt, setOpt] = useState({
        storyline: PILIHAN_CUSTOMER.storyline.slice(0, 3),
        style: PILIHAN_CUSTOMER.style.slice(0, 2),
        hook: PILIHAN_CUSTOMER.hook.slice(0, 4),
        scenes: asal.enjin === 'flow2' ? [2] : asal.enjin === 'resipi' ? [1, 2, 3] : asal.enjin === 'nscene' ? [5] : asal.enjin === 'smart' ? [1, 2] : [1],
        model: [...PILIHAN_CUSTOMER.model],
        avatar: true,
        extra: true,
        extraMaks: 300,
    });
    const ubahOpt = (k, v) => { setOpt((o) => ({ ...o, [k]: v })); setNota(''); };
    const [robot, setRobot] = useState({ workflow: ROBOT_CONTOH[asal.format]?.[0] || '', hasil: 'gabung' });
    const [produkUji, setProdukUji] = useState(PRODUK_UJIAN_CONTOH[0]);
    const [padam, setPadam] = useState({ buka: false, taip: '' });

    const st = STATUS_FA[f.status];
    const goal = GOAL_V2.find((g) => g.id === f.goal);
    const format = FORMAT_V2.find((x) => x.id === f.format);
    const tab2 = [
        { value: 'general', label: 'General' },
        { value: 'card', label: 'Card' },
        { value: 'prompt', label: 'Prompt' },
        { value: 'customer', label: 'Customer options' },
        { value: 'robot', label: 'Robot' },
        ...(f.extras ? [{ value: 'extras', label: LABEL_EXTRAS[f.extras] }] : []),
        { value: 'test', label: 'Test' },
        { value: 'history', label: 'History' },
    ];
    const sebabTakPadam = BINAAN.has(f.id) ? 'Built-in formula — cannot be deleted.'
        : f.akses !== 'admin' ? 'Open to customers — set access to Admin only before deleting.' : '';

    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>
            <button type="button" className="fma-kembali" onClick={onKembali}><ArrowLeft size={15} /> Formula Settings</button>
            <PageHeader
                title={f.nama || 'New formula'}
                sisiTajuk={<Badge tone="warn">Design prototype</Badge>}
                suisBahasa={false}
                actions={(
                    <>
                        {nota && <span className="fma-nota fma-nota--simpan">{nota}</span>}
                        <Btn onClick={simpan}>{baru ? 'Create formula' : 'Save all'}</Btn>
                    </>
                )}
            />
            <div className="fma-meta" style={{ marginTop: -12, marginBottom: 16 }}>
                <Badge>{goal?.nama || 'No goal'}</Badge>
                <Badge>{format?.nama}</Badge>
                <Badge tone={st.tone}>{st.label}</Badge>
                <Badge tone={f.mod === 'classic' ? 'neutral' : 'brand'}>{f.mod === 'classic' ? 'Classic master prompt' : 'Blocks'}</Badge>
            </div>

            <Segmented className="fma-tabs" value={tab} onChange={setTab} options={tab2} />

            {tab === 'prompt' && <FormulaAdminPrompt f={f} onUbah={ubah} onNota={setNota} />}
            {tab === 'extras' && <FormulaAdminExtras jenis={f.extras} onNota={setNota} />}

            {/* ── GENERAL ── */}
            {tab === 'general' && (
                <div className="fma-borang">
                    {f.tiadaDoc && (
                        <p className="fma-nota" style={{ margin: 0 }}>This formula lives in the code only. The first Save creates its record from the built-in values.</p>
                    )}
                    <div className="fma-grid2">
                        <Medan id="fma-nama" label="Name" nota="Shown on the formula card for every customer.">
                            <input id="fma-nama" className="s-input" value={f.nama} maxLength={40} placeholder="Kitchen Demo" onChange={(e) => ubah('nama', e.target.value)} />
                        </Medan>
                        <Medan id="fma-id" label="Formula ID" nota={baru ? 'Lowercase, no spaces. Cannot change later.' : 'Fixed forever — stamped on every card.'}>
                            <input id="fma-id" className="s-input" value={baru ? (f.idBaru || '') : f.id} readOnly={!baru} placeholder="kitchen-demo" onChange={(e) => ubah('idBaru', e.target.value)} />
                        </Medan>
                    </div>
                    <div className="fma-grid2">
                        <Medan id="fma-akses" label="Access">
                            <select id="fma-akses" className="s-select" value={f.akses} onChange={(e) => ubah('akses', e.target.value)}>
                                {AKSES_FA.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                            </select>
                        </Medan>
                        <Medan id="fma-status" label="Status">
                            <select id="fma-status" className="s-select" value={f.status} onChange={(e) => ubah('status', e.target.value)}>
                                {Object.entries(STATUS_FA).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                        </Medan>
                    </div>
                    <div className="fma-grid2">
                        <Medan id="fma-ikon" label="Icon (Material)">
                            <input id="fma-ikon" className="s-input" value={f.ikon} onChange={(e) => ubah('ikon', e.target.value)} />
                        </Medan>
                        <Medan id="fma-warna" label="Colour">
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input id="fma-warna" type="color" value={f.warna} onChange={(e) => ubah('warna', e.target.value)} style={{ width: 44, height: 34, padding: 2, border: '1px solid var(--s-line-strong)', borderRadius: 8, background: 'transparent' }} />
                                <span className="fma-nota">{f.warna}</span>
                            </div>
                        </Medan>
                    </div>
                    <div className="fma-grid2">
                        <Medan id="fma-susun" label="Order" nota="Smaller = earlier in the gallery.">
                            <input id="fma-susun" className="s-input" type="number" min={1} value={f.susunan} onChange={(e) => ubah('susunan', e.target.value)} />
                        </Medan>
                        <Medan label="Prompt type">
                            <span className="fma-nota" style={{ fontSize: 13 }}>{f.mod === 'classic' ? 'Classic (one master prompt, 3 modes)' : 'Blocks'}</span>
                        </Medan>
                    </div>
                    <Medan id="fma-desc" label="Description" nota="Shown to customers instead of the prompt.">
                        <textarea id="fma-desc" className="fma-teks" style={{ minHeight: 60 }} value={f.desc || ''} onChange={(e) => ubah('desc', e.target.value)} />
                    </Medan>

                    {!baru && (
                        <div className="fma-kotak" style={{ borderColor: 'var(--s-stop-soft)' }}>
                            <p className="fma-kotak__tajuk" style={{ color: 'var(--s-stop)' }}><span>Delete formula</span>
                                {!sebabTakPadam && !padam.buka && <Btn variant="ghost" size="sm" icon={<Trash2 size={13} />} onClick={() => setPadam({ buka: true, taip: '' })}>Delete…</Btn>}
                            </p>
                            <p className="fma-nota" style={{ margin: 0 }}>{sebabTakPadam || 'Removes this formula, its prompts and saved versions for everyone. Cards using it fall back to AI8SAAT.'}</p>
                            {padam.buka && (
                                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                                    <input className="s-input" aria-label="Type the formula ID to confirm" placeholder={f.id} value={padam.taip}
                                        onChange={(e) => setPadam((p) => ({ ...p, taip: e.target.value }))} style={{ flex: 1, minWidth: 160 }} />
                                    <Btn variant="ghost" size="sm" onClick={() => setPadam({ buka: false, taip: '' })}>Cancel</Btn>
                                    <Btn size="sm" disabled={padam.taip.trim() !== f.id} onClick={simpan}>Delete permanently</Btn>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── CARD ── */}
            {tab === 'card' && (
                <div className="fma-dua" style={{ gridTemplateColumns: 'minmax(0, 1fr) 200px' }}>
                    <div className="fma-borang">
                        <p className="fma-nota" style={{ margin: 0 }}>What customers see on the Generate tab. Leave a box empty to hide that part.</p>
                        <div className="fma-grid2">
                            <Medan id="fma-nms" label="Line under name — Bahasa Melayu">
                                <input id="fma-nms" className="s-input" maxLength={60} value={f.notaMs} onChange={(e) => ubah('notaMs', e.target.value)} />
                            </Medan>
                            <Medan id="fma-nen" label="Line under name — English">
                                <input id="fma-nen" className="s-input" maxLength={60} value={f.notaEn} onChange={(e) => ubah('notaEn', e.target.value)} />
                            </Medan>
                        </div>
                        <div className="fma-grid2">
                            <Medan id="fma-badge" label="Badge">
                                <input id="fma-badge" className="s-input" maxLength={12} placeholder="BETA" value={f.badge} onChange={(e) => ubah('badge', e.target.value)} />
                            </Medan>
                            <Medan id="fma-durasi" label="Video length">
                                <input id="fma-durasi" className="s-input" maxLength={8} placeholder="0:08" value={f.durasi} onChange={(e) => ubah('durasi', e.target.value)} />
                            </Medan>
                        </div>
                        <div className="fma-grid2" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                            <Medan id="fma-goal" label="Goal (tab)">
                                <select id="fma-goal" className="s-select" value={f.goal} onChange={(e) => ubah('goal', e.target.value)}>
                                    <option value="">None (All tab only)</option>
                                    {GOAL_V2.map((g) => <option key={g.id} value={g.id}>{g.nama}</option>)}
                                </select>
                            </Medan>
                            <Medan id="fma-format" label="Format">
                                <select id="fma-format" className="s-select" value={f.format}
                                    onChange={(e) => { ubah('format', e.target.value); setRobot({ workflow: ROBOT_CONTOH[e.target.value]?.[0] || '', hasil: 'gabung' }); }}>
                                    {FORMAT_V2.map((x) => <option key={x.id} value={x.id}>{x.nama}</option>)}
                                </select>
                            </Medan>
                            <Medan id="fma-pil" label="Pill">
                                <select id="fma-pil" className="s-select" value={f.pil} onChange={(e) => ubah('pil', e.target.value)}>
                                    <option value="">None</option>
                                    {PIL_V2.map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </Medan>
                        </div>
                        <Medan id="fma-poster" label="Preview image or video (URL)">
                            <input id="fma-poster" className="s-input" value={f.poster || ''} placeholder="https://…" onChange={(e) => ubah('poster', e.target.value)} />
                        </Medan>
                    </div>
                    <div>
                        <span className="fma-lbl">Card preview</span>
                        <div className="s-fcard fma-kad" style={{ width: 160 }}>
                            <span className="s-fcard__v">
                                {f.poster ? <img src={f.poster} alt="" /> : <span className="fma-kad__kosong"><span>{f.nama || 'New formula'}</span></span>}
                                {f.pil && <span className="gv2-pilmedia">{f.pil}</span>}
                                {f.durasi && <span className="s-fcard__dur">{f.durasi}</span>}
                            </span>
                            <span className="s-fcard__n">{f.nama || 'New formula'}{f.badge && <span className="gv2-tagnama">{f.badge}</span>}</span>
                            <span className="s-fcard__d">{f.notaEn}</span>
                        </div>
                        <p className="fma-nota" style={{ marginTop: 8 }}>BM: {f.notaMs || '—'}</p>
                    </div>
                </div>
            )}

            {/* ── CUSTOMER OPTIONS ── */}
            {tab === 'customer' && (
                <div className="fma-borang">
                    <p className="fma-nota" style={{ margin: 0 }}>What customers can pick in the prompt box when they use this formula.</p>
                    {f.format === 'video' && (
                        <Medan label="Scenes customers can choose" nota="1–2 scenes = 3 sets per card · 3–5 scenes = 1 set per card. At least one must stay on.">
                            <Pils pilihan={[1, 2, 3, 4, 5]} nilai={opt.scenes} label={(n) => `${n} scene${n > 1 ? 's' : ''} · ${n <= 2 ? '3 sets' : '1 set'}`}
                                onTukar={(v) => ubahOpt('scenes', v.length ? [...v].sort() : opt.scenes)} />
                        </Medan>
                    )}
                    {f.mod === 'blocks' && f.format !== 'image' && (
                        <Medan label="Storylines"><Pils pilihan={PILIHAN_CUSTOMER.storyline} nilai={opt.storyline} onTukar={(v) => ubahOpt('storyline', v)} /></Medan>
                    )}
                    {f.mod === 'blocks' && f.format !== 'text' && (
                        <Medan label="Presentation styles"><Pils pilihan={PILIHAN_CUSTOMER.style} nilai={opt.style} onTukar={(v) => ubahOpt('style', v)} /></Medan>
                    )}
                    {f.mod === 'blocks' && f.format !== 'image' && (
                        <Medan label="Hook types"><Pils pilihan={PILIHAN_CUSTOMER.hook} nilai={opt.hook} onTukar={(v) => ubahOpt('hook', v)} /></Medan>
                    )}
                    {f.format === 'video' && (
                        <Medan label="Video models"><Pils pilihan={PILIHAN_CUSTOMER.model} nilai={opt.model} onTukar={(v) => ubahOpt('model', v)} /></Medan>
                    )}
                    {f.mod === 'classic' && <p className="fma-nota" style={{ margin: 0 }}>Storylines, styles and hook types appear here after converting to blocks.</p>}
                    <div className="fma-kotak" style={{ padding: '4px 14px' }}>
                        {f.format !== 'text' && <BarisSuis nama="Avatar" nota="Customer can put their saved avatar in the image" on={opt.avatar} onTukar={(v) => ubahOpt('avatar', v)} />}
                        <BarisSuis nama="Extra prompt box" nota="Short note from the customer, added to the prompt" on={opt.extra} onTukar={(v) => ubahOpt('extra', v)} />
                        {opt.extra && (
                            <div className="fma-baris">
                                <span className="fma-block__nota">Max characters</span>
                                <input className="s-input" type="number" min={50} max={1000} step={50} value={opt.extraMaks}
                                    onChange={(e) => ubahOpt('extraMaks', e.target.value)} style={{ width: 100 }} />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── ROBOT ── */}
            {tab === 'robot' && (
                <div className="fma-borang">
                    <Medan label="Engine" nota={baru ? 'Chosen once when the formula is created.' : 'Fixed after the formula is created.'}>
                        {baru
                            ? (
                                <select className="s-select" aria-label="Engine" value={f.enjin} onChange={(e) => ubah('enjin', e.target.value)}>
                                    {Object.entries(ENJIN_FA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                            )
                            : <span style={{ fontSize: 13, color: 'var(--s-ink)' }}>{ENJIN_FA[f.enjin]}</span>}
                    </Medan>
                    {f.format === 'text' ? (
                        <Kotak tajuk="No robot">
                            <p className="fma-nota" style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
                                Text formulas put the hooks, script, caption and hashtags straight on the card, each with a Copy button.
                                The customer pastes them into TikTok — no ChatGPT, Gemini or Flow needed.
                            </p>
                        </Kotak>
                    ) : (
                        <>
                            <Medan id="fma-robot" label="Flow robot workflow" nota="Same list as the Workflow page. The robot reads this from the card.">
                                <select id="fma-robot" className="s-select" value={robot.workflow} onChange={(e) => setRobot((r) => ({ ...r, workflow: e.target.value }))}>
                                    {(ROBOT_CONTOH[f.format] || []).map((w) => <option key={w} value={w}>{w}</option>)}
                                </select>
                            </Medan>
                            {f.format === 'video' && (
                                <Medan label="Result for 2+ scenes">
                                    <Segmented value={robot.hasil} onChange={(v) => setRobot((r) => ({ ...r, hasil: v }))}
                                        options={[{ value: 'gabung', label: 'Combine into one video' }, { value: 'asing', label: 'Download each scene' }, { value: 'pelanggan', label: 'Customer chooses' }]} />
                                </Medan>
                            )}
                            {f.format === 'image' && (
                                <p className="fma-nota" style={{ margin: 0 }}>Customer adds their photo by pasting a screenshot (Ctrl+V) or picking a product photo already on the card.</p>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ── TEST ── */}
            {tab === 'test' && (
                <div className="fma-borang">
                    <div className="fma-grid2">
                        <Medan id="fma-uji-produk" label="Test product">
                            <select id="fma-uji-produk" className="s-select" value={produkUji} onChange={(e) => setProdukUji(e.target.value)}>
                                {PRODUK_UJIAN_CONTOH.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </Medan>
                        <Medan label="&nbsp;">
                            <Btn onClick={simpan}>Run test with this formula</Btn>
                        </Medan>
                    </div>
                    <EmptyState title="No test result yet">The result (hooks, image prompt, video prompt per scene, caption) will appear here — same shape as a real card. Tests do not use customer quota.</EmptyState>
                </div>
            )}

            {/* ── HISTORY ── */}
            {tab === 'history' && (
                baru
                    ? <EmptyState title="No versions yet">Every Save keeps the previous version so you can restore it.</EmptyState>
                    : (
                        <div style={{ maxWidth: 900 }}>
                            <Kotak style={{ padding: '4px 14px' }}>
                                {SEJARAH_CONTOH.map((s) => (
                                    <div key={s.versi} className="fma-sejarah" style={{ gridTemplateColumns: '44px 170px minmax(0, 1fr) auto auto' }}>
                                        <b style={{ fontWeight: 600 }}>v{s.versi}</b>
                                        <span className="fma-nota">{s.bila}<br />{s.siapa}</span>
                                        <span>{s.nota}</span>
                                        <span className="fma-nota" title={MOD_PROMPT.map((m) => m.label).join(' / ')}>{s.aksara.map((n) => (n ? n.toLocaleString('en-US') : '—')).join(' / ')} chars</span>
                                        <Btn variant="quiet" size="sm" onClick={() => setNota('Prototype: Restore fills the form only — press Save all to apply.')}>Restore</Btn>
                                    </div>
                                ))}
                            </Kotak>
                            <p className="fma-nota" style={{ marginTop: 8 }}>Keeps the 20 newest versions; older ones are removed after 30 days. Restore fills the form only — press Save all to apply.</p>
                        </div>
                    )
            )}
        </div>
    );
}
